import {
  songFromRecording,
  typeaheadQuery,
  typeaheadReady,
  usageLine,
  usedByLine,
  type CatalogSong,
  type CatalogUsage,
} from '@church/domain'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useApi } from '../api-context.tsx'
import { useAccess } from '../access/useAccess.ts'
import { useDebounced } from '../people/usePeople.ts'
import {
  recordingFacts,
  songCatalogKeys,
  useSongCatalogStatus,
  type RecordingFacts,
} from './useSongCatalog.ts'
import { useCreateSong } from './useServices.ts'

/**
 * The shared song catalog on the screens that add songs (docs/15-MUSIC-AND-AI.md):
 * suggestions from Apple Music while a Songs Editor types a new song's title,
 * choosing one to fill the song in, and making a song from a recording in
 * one go (the plan item's song picker, Discover's "+ Add").
 */

/** A recording offered (typing, Discover), as a row reads it. */
export interface CatalogHitView {
  recording: CatalogSong
  christian: boolean
  usage: CatalogUsage | null
  /** The church's song that is this recording, when it has one. */
  songId: string | null
  facts: RecordingFacts
  /** "Used by 12 churches" (from three churches up), or null. */
  usedBy: string | null
  /** "Sung by 12 churches this Sunday · 40 this month", or null. */
  usageLine: string | null
}

export function hitView(hit: {
  recording: CatalogSong
  christian: boolean
  usage: CatalogUsage | null
  songId: string | null
}): CatalogHitView {
  return {
    ...hit,
    facts: recordingFacts(hit.recording),
    usedBy: usedByLine(hit.usage),
    usageLine: usageLine(hit.usage),
  }
}

/**
 * Suggestions for what is being typed as a song's title: once a first word
 * is typed (`typeaheadReady`), a quarter of a second after the typing
 * stops, the previous answer kept on screen while the next loads; an answer
 * no longer wanted is cancelled. For Songs Editors where Apple Music is set
 * up; nothing otherwise.
 */
export function useCatalogTypeahead(raw: string, { enabled = true }: { enabled?: boolean } = {}) {
  const api = useApi()
  const editor = useAccess().hasSomewhere('songs', 'editor')
  const appleMusic = useSongCatalogStatus().data?.enabled === true
  const q = useDebounced(typeaheadQuery(raw), 250)
  const available = editor && appleMusic
  const on = enabled && available && typeaheadReady(raw) && q.length >= 2
  const query = useQuery({
    queryKey: songCatalogKeys.typeahead(q),
    enabled: on,
    queryFn: async ({ signal }) => {
      const { data, error } = await api.api.v1.services['song-catalog'].search.get({
        query: { q },
        fetch: { signal },
      })
      if (error) throw error
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
  return {
    /** Suggestions can be offered here at all (a Songs Editor, Apple Music on). */
    available,
    /** Enough is typed to suggest: the list shows (empty while the first answer loads). */
    shown: on,
    loading: on && query.isFetching,
    error: on ? query.error : null,
    results: on ? (query.data?.results ?? []).map(hitView) : [],
  }
}

/** What choosing a recording fills in, fetched once (with its album's copyright). */
function usePrefill() {
  const api = useApi()
  const queryClient = useQueryClient()
  return (catalogId: string) =>
    queryClient.fetchQuery({
      queryKey: songCatalogKeys.prefill(catalogId),
      staleTime: 60 * 60_000,
      queryFn: async () => {
        const { data, error } = await api.api.v1.services['song-catalog']
          .recordings({ catalogId })
          .get()
        if (error) throw error
        return data
      },
    })
}

/**
 * The New song panel: its fields, suggestions from Apple Music under the
 * title while it is typed, and choosing one — which fills the title
 * (cleaned), the artist (as Apple writes it), the author (its composers),
 * the copyright (its album's) and the length, all still editable, and links
 * the new song to that recording (`confirmed`). "Not this one" lets the
 * choice go and takes back what it filled in. Fresh each time it opens; it
 * may open with a recording already chosen (Discover's + Add).
 */
export function useNewSongForm({
  open,
  from = null,
}: {
  open: boolean
  /** A recording to start from. */
  from?: CatalogHitView | null
}) {
  const create = useCreateSong()
  const prefill = usePrefill()
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [artist, setArtist] = useState('')
  const [ccliNumber, setCcliNumber] = useState('')
  const [key, setKey] = useState('')
  const [copyright, setCopyright] = useState('')
  const [lengthSeconds, setLengthSeconds] = useState<number | null>(null)
  const [chosen, setChosen] = useState<CatalogHitView | null>(null)
  /** What choosing filled in, so "Not this one" takes back only that. */
  const [filled, setFilled] = useState<Record<string, string>>({})
  /** The list is put away (Escape, a choice) until the title is typed again. */
  const [listClosed, setListClosed] = useState(false)
  const [loadingChoice, setLoadingChoice] = useState(false)
  const [choiceError, setChoiceError] = useState<unknown>(null)
  /** The recording chosen last: a slower answer for an earlier choice is dropped. */
  const latest = useRef<string | null>(null)
  const typeahead = useCatalogTypeahead(title, { enabled: open && !chosen && !listClosed })

  const fill = (values: {
    title: string
    artist: string | null
    author: string | null
    copyright: string | null
    lengthSeconds: number | null
  }) => {
    setTitle(values.title)
    setArtist(values.artist ?? '')
    setAuthor(values.author ?? '')
    setCopyright(values.copyright ?? '')
    setLengthSeconds(values.lengthSeconds)
    setFilled({
      title: values.title,
      artist: values.artist ?? '',
      author: values.author ?? '',
      copyright: values.copyright ?? '',
    })
  }

  function choose(hit: CatalogHitView) {
    latest.current = hit.recording.id
    setChosen(hit)
    setListClosed(true)
    setChoiceError(null)
    // At once from what the row knows; the album's copyright follows.
    fill(songFromRecording(hit.recording))
    setLoadingChoice(true)
    prefill(hit.recording.id)
      .then((found) => {
        const line = found.song.copyright
        if (latest.current !== hit.recording.id || !line) return
        setCopyright((typed) => typed || line)
        setFilled((was) => ({ ...was, copyright: line }))
      })
      .catch((error: unknown) => {
        if (latest.current === hit.recording.id) setChoiceError(error)
      })
      .finally(() => {
        if (latest.current === hit.recording.id) setLoadingChoice(false)
      })
  }

  // A fresh form each time it opens.
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setTitle('')
      setAuthor('')
      setArtist('')
      setCcliNumber('')
      setKey('')
      setCopyright('')
      setLengthSeconds(null)
      setChosen(null)
      setFilled({})
      setListClosed(false)
      setChoiceError(null)
      create.reset()
    }
  }
  // Opened with a recording already chosen (Discover's + Add): chosen once it shows.
  const fromId = open ? (from?.recording.id ?? null) : null
  useEffect(() => {
    if (!open) latest.current = null
    else if (fromId && from && latest.current !== fromId) choose(from)
  }, [open, fromId, from])

  return {
    title,
    setTitle: (next: string) => {
      setTitle(next)
      // Typing again offers suggestions again.
      setListClosed(false)
    },
    author,
    setAuthor,
    artist,
    setArtist,
    ccliNumber,
    setCcliNumber,
    key,
    setKey: (next: string) => setKey((current) => (current === next ? '' : next)),
    copyright,
    setCopyright,
    lengthSeconds,
    typeahead,
    /** The suggestions are put away until the title is typed again (Escape). */
    closeList: () => setListClosed(true),
    chosen,
    loadingChoice,
    choiceError,
    choose,
    /** "Not this one": the choice goes, and what it filled in that was not changed since. */
    clearChoice() {
      latest.current = null
      setLoadingChoice(false)
      setChosen(null)
      setChoiceError(null)
      if (artist === filled.artist) setArtist('')
      if (author === filled.author) setAuthor('')
      if (copyright === filled.copyright) setCopyright('')
      setLengthSeconds(null)
      setFilled({})
      setListClosed(true)
    },
    canSubmit: title.trim().length > 0,
    pending: create.isPending,
    error: create.error,
    submit(onCreated: (song: { id: string; title: string }) => void) {
      create.mutate(
        {
          title: title.trim(),
          author: author.trim() || null,
          artist: artist.trim() || null,
          ccliNumber: ccliNumber.trim() || null,
          key: key || null,
          copyright: copyright.trim() || null,
          lengthSeconds: chosen ? lengthSeconds : null,
          catalogId: chosen?.recording.id ?? null,
        },
        { onSuccess: (song) => song && onCreated({ id: song.id, title: song.title }) },
      )
    },
  }
}

export type NewSongForm = ReturnType<typeof useNewSongForm>

/**
 * A song made from a recording in one go (the song picker's "From Apple
 * Music"): filled in as choosing it in New song would be, linked
 * `confirmed`. A recording already in the library is that song.
 */
export function useAddRecording() {
  const create = useCreateSong()
  const prefill = usePrefill()
  return useMutation({
    mutationFn: async (hit: CatalogHitView): Promise<{ id: string; title: string }> => {
      if (hit.songId) return { id: hit.songId, title: songFromRecording(hit.recording).title }
      const found = await prefill(hit.recording.id).catch(() => null)
      const song = found?.song ?? songFromRecording(hit.recording)
      const made = await create.mutateAsync({
        title: song.title,
        artist: song.artist,
        author: song.author,
        copyright: song.copyright,
        lengthSeconds: song.lengthSeconds,
        catalogId: hit.recording.id,
      })
      return { id: made.id, title: made.title }
    },
  })
}
